import { AbstractControl, FormControlState, FormGroup } from '@angular/forms';
import { combineLatest, map, startWith } from 'rxjs';

// Get the type of the raw value of an abstract control
type UnpackedControl<T extends AbstractControl> =
  T extends AbstractControl<any, infer K>
    ? Exclude<K, FormControlState<K>>
    : never;

/**
 * Takes a FormGroup and a set of its keys, and creates an observable that emits
 * their combined keyvalues any time one of their values changes.
 * @param form FormGroup
 * @param watchers Keys to watch (no nested support)
 */
export function watchFormKeys<
  T extends { [K in keyof T]: AbstractControl<UnpackedControl<T[K]>, any> },
>(form: FormGroup<T>, ...watchers: (keyof T)[]) {
  // Combine the control's `valueChanges` observables
  return combineLatest(
    watchers.map((key) =>
      form.controls[key].valueChanges
        // Somehow typing is broken and only accepts 0 arguments the first call.
        .pipe()
        // Preload each valueChanges pipe with the current control value.
        .pipe(startWith(form.controls[key].value)),
    ),
  ).pipe(
    map(
      // Combine into keyvalue object.
      (values) =>
        Object.fromEntries(
          watchers.map((key, index) => [key, values[index]]),
        ) as { [K in keyof T]: UnpackedControl<T[K]> },
    ),
  );
}
